import React, { useState, useEffect } from 'react';
import { toastService } from '../services/toast';
import { BookOpenIcon, AcademicCapIcon } from '@heroicons/react/outline';
import { ordersApi } from '../services/api/orders.api';
import axiosInstance from '../config/axios';

// مكون Skeleton للدورات والكتب
export const CoursesSkeleton = () => {
    return (
        <div className="space-y-8">
            {/* دوراتي Skeleton */}
            <div>
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                    <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="h-6 w-6 bg-gray-200 dark:bg-gray-600 rounded-full ml-2"></div>
                                <div className="h-5 w-24 bg-gray-200 dark:bg-gray-600 rounded"></div>
                            </div>
                            <div className="h-4 w-14 bg-gray-200 dark:bg-gray-600 rounded"></div>
                        </div>
                    </div>
                    <div className="p-0 sm:p-6 mt-4">
                        <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
                            {[...Array(3)].map((_, i) => (
                                <div key={`course-skeleton-${i}`} className="animate-pulse bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                    <div className="p-4">
                                        <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded mb-2 w-3/4"></div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded mb-2 w-1/2"></div>
                                        <div className="mt-4">
                                            <div className="h-9 bg-gray-200 dark:bg-gray-600 rounded-md w-full"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* كتبي Skeleton */}
            <div className="mt-8">
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                    <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="h-6 w-6 bg-gray-200 dark:bg-gray-600 rounded-full ml-2"></div>
                                <div className="h-5 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                            </div>
                            <div className="h-4 w-14 bg-gray-200 dark:bg-gray-600 rounded"></div>
                        </div>
                    </div>
                    <div className="p-0 sm:p-6 mt-4">
                        <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
                            {[...Array(3)].map((_, i) => (
                                <div key={`book-skeleton-${i}`} className="animate-pulse flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                    <div className="relative w-32 h-40 bg-gray-200 dark:bg-gray-600"></div>
                                    <div className="flex-1 p-4">
                                        <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded mb-2 w-3/4"></div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded mb-2 w-1/2"></div>
                                        <div className="space-y-2">
                                            <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
                                            <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
                                            <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16 mt-2"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function MyCourses() {
    const [loading, setLoading] = useState(true);
    const [coursesLoading, setCoursesLoading] = useState(true);
    const [courses, setCourses] = useState([]);
    const [books, setBooks] = useState([]);

    useEffect(() => {
        fetchMyBooks();
        fetchMyCourses();
    }, []);

    const fetchMyCourses = async () => {
        try {
            setCoursesLoading(true);
            // نجيب طلبات الدورات الخاصة بالمستخدم الحالي فقط
            const response = await ordersApi.getUserOrders('course');
            // Process course orders response
            
            // نفلتر الطلبات المقبولة فقط
            const acceptedOrders = response.filter(order => order.status === 'accepted' || order.status === 'approved');
            // Filter accepted orders
            
            // نجمع الدورات من الطلبات المقبولة
            const myCourses = acceptedOrders.flatMap(order => {
                // Process order items
                return order.items.map(item => {
                    // Process course item
                    // نستخدم course_id من استجابة API
                    const courseId = item.course_id || item.courseId || order.id;
                    // Use course ID
                    return {
                        id: courseId,
                        title: item.courseTitle || 'دورة غير معروفة',
                        purchaseDate: order.created_at
                    };
                });
            });

            setCourses(myCourses);
        } catch (error) {
            // Handle course fetch error
            toastService.error('حدث خطأ في تحميل الدورات');
        } finally {
            setCoursesLoading(false);
        }
    };

    const fetchMyBooks = async () => {
        try {
            setLoading(true);
            // نجيب طلبات الكتب المكتملة الخاصة بالمستخدم الحالي فقط
            const response = await ordersApi.getUserOrders('book');
            // نفلتر الطلبات المقبولة فقط
            const completedOrders = response.filter(order => order.status === 'accepted');
            
            // نجمع الكتب من الطلبات المكتملة
            const myBooks = completedOrders.flatMap(order => 
                order.items.map(item => ({
                    id: item.id,
                    title: item.bookTitle,
                    author: item.author || 'المؤلف',
                    cover: item.cover || '',
                    format: 'PDF',
                    purchaseDate: order.created_at,
                    bookId: item.bookId
                }))
            );

            setBooks(myBooks);
        } catch (error) {
            // Handle book fetch error
            toastService.error('حدث خطأ في تحميل الكتب');
        } finally {
            setLoading(false);
        }
    };

    const EmptyState = ({ icon: Icon, title, description }) => (
        <div className="text-center py-12">
            <Icon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-base font-medium text-gray-900 dark:text-white">{title}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
    );

    return (
        <div className="space-y-8">
            {/* الدورات */}
            <div>
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                    <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <AcademicCapIcon className="h-6 w-6 text-gray-500 dark:text-gray-400 ml-2" />
                                <h2 className="text-lg font-medium text-gray-900 dark:text-white">دوراتي</h2>
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {courses.length} {courses.length === 1 ? 'دورة' : 'دورات'}
                            </span>
                        </div>
                    </div>
                    <div className="p-0 sm:p-6 mt-4">
                        {coursesLoading ? (
                            <CoursesSkeleton />
                        ) : courses.length > 0 ? (
                            <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
                                {courses.map((course) => (
                                    <div key={course.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-sm transition-shadow">
                                        <div className="p-4">
                                            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
                                                {course.title}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                                تاريخ الشراء: {new Date(course.purchaseDate).toLocaleDateString('ar', {day: '2-digit', month: '2-digit', year: 'numeric'})}
                                            </p>
                                            <div className="mt-4">
                                                <a href={`/courses/${course.id}`} className="block w-full bg-blue-600 dark:bg-blue-600 text-white dark:text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-800 dark:hover:bg-blue-600 transition-colors duration-200 text-center">
                                                    متابعة الدورة
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyState
                                icon={AcademicCapIcon}
                                title="لا توجد دورات حتى الآن"
                                description="اكتشف دوراتنا المتنوعة وابدأ رحلة التعلم"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* الكتب */}
            <div className="mt-8">
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
                    <div className="prose dark:prose-invert max-w-none text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <BookOpenIcon className="h-6 w-6 text-gray-500 dark:text-gray-400 ml-2" />
                                <h2 className="text-lg font-medium text-gray-900 dark:text-white">كتبي</h2>
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {books.length} {books.length === 1 ? 'كتاب' : 'كتب'}
                            </span>
                        </div>
                    </div>
                    <div className="p-0 sm:p-6 mt-4">
                        {loading ? (
                            <CoursesSkeleton />
                        ) : books.length > 0 ? (
                            <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
                                {books.map((book) => {
                                    // Process book object
                                    const realBookId = book.bookId || book.id;
                                    if (!realBookId) {
                                        // No valid book ID found
                                    }
                                    return (
                                        <div key={realBookId} className="flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-sm transition-shadow">
                                            <div className="relative w-32 h-full">
                                                <img
                                                    src={book.cover || 'https://via.placeholder.com/128x176?text=كتاب'}
                                                    alt={book.title}
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://via.placeholder.com/128x176?text=كتاب';
                                                    }}
                                                />
                                            </div>
                                            <div className="flex-1 p-4">
                                                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
                                                    {book.title}
                                                </h3>
                                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                                                    المؤلف: {book.author}
                                                </p>
                                                <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
                                                    <p>الصيغة: PDF</p>
                                                    <p>تاريخ الشراء: {new Date(book.purchaseDate).toLocaleDateString('ar')}</p>
                                                    <button
                                                        disabled={!realBookId}
                                                        onClick={async () => {
                                                            try {
                                                                if (!realBookId) {
                                                                    // No valid book ID for download
                                                                    toastService.error('لا يوجد معرف صالح لتحميل الكتاب');
                                                                    return;
                                                                }
                                                                
                                                                // Book info for download
                                                                
                                                                // استخدام axiosInstance بدل fetch
                                                                toastService.info('جاري تحضير الكتاب للتحميل...');
                                                                
                                                                try {
                                                                    const response = await axiosInstance.get(`/api/books/${realBookId}/download`);
                                                                    // Process download API response
                                                                    
                                                                    if (response.data && response.data.download_url) {
                                                                        // تعديل الرابط إذا كان يستخدم localhost
                                                                        let downloadUrl = response.data.download_url;
                                                                        
                                                                        // استبدال localhost بالـ IP الحالي إذا كان مختلفًا
                                                                        if (downloadUrl.includes('localhost') && !window.location.href.includes('localhost')) {
                                                                            const currentHost = window.location.origin;
                                                                            downloadUrl = downloadUrl.replace(/http:\/\/localhost(:\d+)?/g, currentHost);
                                                                            // Modified download URL
                                                                        }
                                                                        
                                                                        // تحميل الملف في نفس الصفحة
                                                                        window.location.href = downloadUrl;
                                                                        toastService.success('تم بدء تحميل الكتاب');
                                                                    } else {
                                                                        toastService.error(response.data?.message || 'تعذر الحصول على رابط التحميل');
                                                                    }
                                                                } catch (err) {
                                                                    // Download API error occurred
                                                                    // Error response details
                                                                    
                                                                    if (err.response?.status === 401) {
                                                                        toastService.error('انتهت صلاحية رابط التحميل');
                                                                    } else if (err.response?.status === 404) {
                                                                        toastService.error(err.response?.data?.message || 'الكتاب غير موجود');
                                                                    } else {
                                                                        toastService.error('حدث خطأ أثناء تحميل الكتاب');
                                                                    }
                                                                }
                                                            } catch (err) {
                                                                // General download error
                                                                toastService.error('حدث خطأ أثناء تحميل الكتاب');
                                                            }
                                                        }}
                                                        className="inline-flex items-center text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mt-2"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                        </svg>
                                                        تحميل الكتاب
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyState
                                icon={BookOpenIcon}
                                title="لا توجد كتب حتى الآن"
                                description="اكتشف مكتبتنا الغنية بالكتب المتنوعة"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* إرشادات للمستخدم */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
                <h4 className="text-base font-medium text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    معلومات مفيدة
                </h4>
                <ul className="space-y-3 text-sm text-blue-900 dark:text-blue-100">
                    <li className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        يمكنك متابعة الدورات من هنا
                    </li>
                    <li className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        الكتب التي اشتريتها متاحة للتحميل في أي وقت
                    </li>
                    <li className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        تصفح كل الدورات والكتب من القائمة الرئيسية
                    </li>
                </ul>
            </div>
        </div>
    );
}
